import React, { lazy, Suspense, useState, useCallback, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth, signOut, resolveAuthEmail, hydrateAuthUserFromApi, getAuthState } from '../components/AuthView';

const AuthView = lazy(() => import('../components/AuthView'));

import { startAuthInitWatchdog, stopAuthInitWatchdog } from '../lib/authInitWatchdog';
import { ADMIN_EMAILS, isWhitelistedAdminEmail } from '../constants/adminAccess';
import { OPS_ADMIN_NAV } from '../constants/opsAdminNav';

const GOLD = '#C9A84C';

const OPS_ADMIN_ROLES = ['owner', 'developer'];

function AdminGatePanel({ email, onSignedOut }) {
  const [signingOut, setSigningOut] = useState(false);
  const displayEmail = email || '(email not found in session — try signing out and back in)';

  const handleSignOut = useCallback(async () => {
    setSigningOut(true);
    try {
      await signOut();
      onSignedOut();
    } catch {
      setSigningOut(false);
    }
  }, [onSignedOut]);

  return (
    <div style={{
      maxWidth: 420,
      width: '100%',
      padding: '32px 28px',
      borderRadius: 16,
      backgroundColor: '#fff',
      boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
      textAlign: 'center',
    }}>
      <p style={{ color: '#1a1a2e', fontSize: 17, fontWeight: 700, margin: '0 0 8px' }}>
        Admin access required
      </p>
      <p style={{ color: '#666', fontSize: 14, lineHeight: 1.55, margin: '0 0 16px' }}>
        Maestro Ops is limited to authorized admin accounts.
      </p>
      <p style={{
        color: '#444',
        fontSize: 13,
        margin: '0 0 20px',
        padding: '10px 12px',
        borderRadius: 8,
        backgroundColor: '#f5f5f5',
        wordBreak: 'break-all',
      }}>
        Signed in as <strong>{displayEmail}</strong>
      </p>
      <p style={{ color: '#888', fontSize: 12, lineHeight: 1.5, margin: '0 0 20px' }}>
        Allowed: {ADMIN_EMAILS.join(', ')}
      </p>
      <button
        type="button"
        onClick={handleSignOut}
        disabled={signingOut}
        style={{
          width: '100%',
          padding: '11px 16px',
          borderRadius: 10,
          border: 'none',
          backgroundColor: GOLD,
          color: '#1a1a2e',
          fontSize: 14,
          fontWeight: 700,
          cursor: signingOut ? 'wait' : 'pointer',
          opacity: signingOut ? 0.7 : 1,
        }}
      >
        {signingOut ? 'Signing out…' : 'Sign out and use a different account'}
      </button>
      <button
        type="button"
        onClick={() => window.location.replace('/app')}
        style={{
          marginTop: 12,
          background: 'none',
          border: 'none',
          color: '#888',
          fontSize: 13,
          cursor: 'pointer',
          textDecoration: 'underline',
        }}
      >
        Back to Maestro
      </button>
    </div>
  );
}

const NAV_ITEMS = OPS_ADMIN_NAV;

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, isLoading, adminRole } = useAuth();
  const [showSignIn, setShowSignIn] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);

  useEffect(() => {
    startAuthInitWatchdog(getAuthState().isLoading);
    return () => stopAuthInitWatchdog();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setSessionChecked(true);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      if (!resolveAuthEmail() || !adminRole) {
        await hydrateAuthUserFromApi();
      }
      if (!cancelled) setSessionChecked(true);
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated, adminRole]);

  const resolvedEmail = resolveAuthEmail() || user?.email || null;
  const isAdmin = isAuthenticated && (
    OPS_ADMIN_ROLES.includes(adminRole)
    || isWhitelistedAdminEmail(resolvedEmail)
  );

  if ((isLoading && !isAuthenticated) || (isAuthenticated && !sessionChecked)) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        <p style={{ color: '#888', fontSize: 15 }}>Checking your session…</p>
      </div>
    );
  }

  if (!isAuthenticated || showSignIn) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        <Suspense fallback={<p style={{ color: '#888', fontSize: 15 }}>Loading…</p>}>
          <AuthView
            onAuthenticated={() => {
              setShowSignIn(false);
              window.location.reload();
            }}
            isModal={false}
          />
        </Suspense>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f5f5f5', fontFamily: 'system-ui, -apple-system, sans-serif', padding: 24 }}>
        <AdminGatePanel email={resolvedEmail} onSignedOut={() => setShowSignIn(true)} />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f5f5f5', fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      {/* Top bar */}
      <header style={{
        height: 56,
        backgroundColor: '#1a1a2e',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0 24px',
        borderBottom: '1px solid rgba(255,255,255,0.06)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          <button
            onClick={() => navigate('/app')}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 8, padding: 0,
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#C9A84C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            <span style={{ fontSize: 15, fontWeight: 800, color: '#C9A84C', letterSpacing: '-0.02em' }}>Maestro Ops</span>
          </button>

          <nav style={{ display: 'flex', gap: 4 }}>
            {NAV_ITEMS.map(item => {
              const active = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
              return (
                <button
                  key={item.path}
                  onClick={() => navigate(item.path)}
                  style={{
                    background: active ? 'rgba(201,168,76,0.15)' : 'none',
                    border: 'none',
                    borderRadius: 8,
                    padding: '7px 14px',
                    fontSize: 13,
                    fontWeight: active ? 700 : 500,
                    color: active ? '#C9A84C' : 'rgba(255,255,255,0.55)',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  {item.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', fontWeight: 500 }}>
            {resolvedEmail || user?.email}
          </span>
        </div>
      </header>

      {/* Content */}
      <main style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 24px 80px' }}>
        <Outlet />
      </main>
    </div>
  );
}
